// ═══════════════════════════════════════════════════════════════
// xcFilterBar.js — XC-FILTER platform component
// Path: src/components/xcFilterBar.js
//
// S4.A (May 18, 2026) — NEW FILE. Port of FILTER_CONFIGS + xcFilterBar
// from sandbox v7.2 (lines 541-829). Converted from vanilla-JS
// HTML-returning function to a real React component.
//
// CANON
//   - Taxonomy v11.1 PLATFORM-COMPONENTS row 9: "Embedded per-view
//     (not standalone nav). Persistent top-of-page filter on all
//     list/table views. Filter fields configured per view. No nav item."
//   - Strategy v1.11.1 §3b row 10: XC-FILTER · Team & Roles · Embedded
//     per-view · "Persistent top-of-page filter on all list/table views.
//     Not standalone nav."
//
// WHAT IT IS
//   Three exports:
//   1. FILTER_CONFIGS — object keyed by viewId. Each entry has a label
//      and a fields array. Each field has id/label/options. 21 viewIds
//      shipped, matching the sandbox.
//   2. useFilterState(viewId) — React hook returning [values, setValue,
//      reset]. Per-view state, lives in component memory (NOT a window
//      global like the sandbox; React idiom).
//   3. XcFilterBar — React component. Props: viewId. Renders the chip
//      row with selects. Returns null if viewId has no config.
//
// NO CONSUMERS WIRED IN S4
//   This is shipped dormant. First production consumer lands at S5
//   (XC-DOC list view) or S8 (XC-NET vendor list), whichever real list
//   view ships first. Smoke harness in PlaceholderDashboard.jsx renders
//   <XcFilterBar viewId="opr-props" /> behind ?xcfilter-smoke=1.
//
// STYLE
//   Matches ShellNav.jsx idiom: inline styles, verticalstack cyan
//   (#00B8D0) active accent, Arial font family. No external CSS.
//
// AS REFERENCES
//   - AS-S4-001 — two-array split confirms XC items live in ShellNav,
//     XC-FILTER lives in this separate file.
//   - AS-S4-003 — React component (useState), not window globals.
//   - AS-S4-005 — visual treatment consistent with rest of nav surface.
// ═══════════════════════════════════════════════════════════════

import React from 'react';

// ── Per-view filter configurations ──────────────────────────────────────
// Sourced verbatim from sandbox v7.2 FILTER_CONFIGS (lines 541-700).
// Each viewId entry: { label, fields: [{ id, label, options }] }
// First option in every field is the "All" sentinel (no filter applied).

export const FILTER_CONFIGS = {
  // ── Acquire ───────────────────────────────────────────────────────────
  'cases': {
    label: 'Business Cases',
    fields: [
      { id: 'status',  label: 'Status',  options: ['All', 'Active', 'Closed', 'Pending'] },
      { id: 'capital', label: 'Capital', options: ['All', '<$1M', '$1-5M', '$5M+'] },
      { id: 'market',  label: 'Market',  options: ['All', 'OH Corridor', 'TX', 'KS', 'VA'] },
    ],
  },
  'pipeline': {
    label: 'Deal Pipeline',
    fields: [
      { id: 'stage', label: 'Stage', options: ['All', 'LOI', 'Underwriting', 'PSA', 'Closing'] },
      { id: 'type',  label: 'Type',  options: ['All', 'SFR', 'Multi', 'Commercial', 'Land'] },
    ],
  },

  // ── Develop ───────────────────────────────────────────────────────────
  'dev-overview': {
    label: 'Develop Portfolio',
    fields: [
      { id: 'stack',     label: 'Stack',     options: ['All', 'Buildstack', 'Launchstack'] },
      { id: 'occupancy', label: 'Occupancy', options: ['All', '0-50%', '50-90%', '90%+'] },
    ],
  },
  'dev-bidroom': {
    label: 'Bid Room',
    fields: [
      { id: 'package', label: 'Package', options: ['All', 'GC', 'Subs', 'Materials'] },
      { id: 'status',  label: 'Status',  options: ['All', 'Open', 'Awarded', 'Closed'] },
    ],
  },
  'dev-cmlog': {
    label: 'CM Log',
    fields: [
      { id: 'package', label: 'Package',   options: ['All', 'HTC', 'OHPTC', 'Core'] },
      { id: 'item',    label: 'Item Type', options: ['All', 'Change Order', 'RFI', 'Submittal'] },
    ],
  },

  // ── Operate ───────────────────────────────────────────────────────────
  'opr-props': {
    label: 'Properties',
    fields: [
      { id: 'entity', label: 'Entity', options: ['All', 'A-1 APS', 'Retriever Ridge', 'Red Oak', 'Personal'] },
      { id: 'type',   label: 'Type',   options: ['All', 'SFR', 'Multi', 'STR', 'Commercial'] },
      { id: 'status', label: 'Status', options: ['All', 'Occupied', 'Vacant', 'Rehab'] },
    ],
  },
  'opr-leases': {
    label: 'Leases',
    fields: [
      { id: 'type',     label: 'Type',     options: ['All', 'LTR', 'STR', 'NNN', 'Commercial'] },
      { id: 'expiring', label: 'Expiring', options: ['All', '30 days', '90 days', '180 days'] },
    ],
  },
  'opr-wos': {
    label: 'Work Orders',
    fields: [
      { id: 'priority', label: 'Priority', options: ['All', 'High', 'Medium', 'Low'] },
      { id: 'status',   label: 'Status',   options: ['All', 'Open', 'In Progress', 'Closed'] },
    ],
  },

  // ── Maintain ──────────────────────────────────────────────────────────
  'mnt-overview': {
    label: 'Maintainstack Overview',
    fields: [
      { id: 'entity', label: 'Entity', options: ['All', 'A-1 APS', 'Retriever Ridge', 'Potters'] },
      { id: 'risk',   label: 'Risk',   options: ['All', 'High', 'Medium', 'Low'] },
    ],
  },
  'mnt-register': {
    label: 'Asset Register',
    fields: [
      { id: 'category',  label: 'Category',  options: ['All', 'HVAC', 'Plumbing', 'Electrical', 'Roof', 'Pool'] },
      { id: 'condition', label: 'Condition', options: ['All', '<40', '40-60', '60-80', '80+'] },
      { id: 'age',       label: 'Age',       options: ['All', '<5yr', '5-10yr', '10+ yr'] },
    ],
  },
  'mnt-wos': {
    label: 'Work Orders',
    fields: [
      { id: 'priority', label: 'Priority', options: ['All', 'High', 'Medium', 'Low'] },
      { id: 'status',   label: 'Status',   options: ['All', 'Open', 'In Progress', 'Closed'] },
      { id: 'vendor',   label: 'Vendor',   options: ['All', 'EverGuard', 'BBow', 'Stark', 'TriState'] },
    ],
  },

  // ── Reposition ────────────────────────────────────────────────────────
  'rep-overview': {
    label: 'Holdstack Overview',
    fields: [
      { id: 'subtype', label: 'Sub-type', options: ['All', 'Lot Bank', 'Timber', 'HOA Stalled', 'Tax Sale'] },
      { id: 'status',  label: 'Status',   options: ['All', 'Active', 'Watchlist', 'Reactivating'] },
    ],
  },

  // ── Realize ───────────────────────────────────────────────────────────
  'rzl-overview': {
    label: 'Exitstack Overview',
    fields: [
      { id: 'type',   label: 'Type',   options: ['All', 'Sale', '1031', 'Refi', 'Close-Out'] },
      { id: 'status', label: 'Status', options: ['All', 'Analysis', 'Pipeline', 'Active', 'Closed'] },
    ],
  },

  // ── Universal cross-cutting views ─────────────────────────────────────
  'xc-network': {
    label: 'Network',
    fields: [
      { id: 'tier',     label: 'Tier',     options: ['All', 'T1', 'T2', 'T3'] },
      { id: 'category', label: 'Category', options: ['All', 'Trades', 'Legal', 'Finance', 'Brokerage', 'Other'] },
    ],
  },
  'xc-marketplace': {
    label: 'Marketplace',
    fields: [
      { id: 'category', label: 'Category', options: ['All', 'Vendors', 'Materials', 'Services', 'Inspections'] },
      { id: 'tier',     label: 'Tier',     options: ['All', 'T1', 'T2', 'T3'] },
    ],
  },
  'xc-calendar': {
    label: 'Calendar',
    fields: [
      { id: 'window', label: 'Window', options: ['All', 'Today', '7 days', '30 days', '90 days'] },
      { id: 'type',   label: 'Type',   options: ['All', 'Closings', 'Inspections', 'PM', 'WO', 'Triggers'] },
      { id: 'party',  label: 'Party',  options: ['All', 'Assigned', 'Unassigned'] },
    ],
  },
  'xc-triggers': {
    label: 'Triggers',
    fields: [
      { id: 'state', label: 'State', options: ['All', 'Monitoring', 'Approaching', 'Triggered', 'Fired'] },
      { id: 'type',  label: 'Type',  options: ['All', 'Lease', 'Renewal', 'Condition', 'Reserve', 'Hold'] },
    ],
  },
  'xc-documents': {
    label: 'Documents',
    fields: [
      { id: 'type',   label: 'Type',   options: ['All', 'Contract', 'Invoice', 'Permit', 'Compliance', 'Other'] },
      { id: 'recent', label: 'Recent', options: ['All', '7 days', '30 days', '90 days'] },
    ],
  },
  'xc-messages': {
    label: 'Messages',
    fields: [
      { id: 'state',   label: 'State',   options: ['All', 'Unread', 'Read', 'Replied'] },
      { id: 'channel', label: 'Channel', options: ['All', 'SMS', 'Email'] },
    ],
  },
  'xc-reports': {
    label: 'Reports',
    fields: [
      { id: 'category', label: 'Category', options: ['All', 'Process Output', 'Financial', 'Operational', 'Strategic', 'Compliance'] },
      { id: 'status',   label: 'Status',   options: ['All', 'Draft', 'Final', 'Signed', 'Archived'] },
    ],
  },
  'xc-team': {
    label: 'Team',
    fields: [
      { id: 'fill',  label: 'Fill',  options: ['All', 'Filled', 'Unassigned'] },
      { id: 'level', label: 'Level', options: ['All', 'Entity', 'Parcel', 'Structure'] },
    ],
  },
};

// ── Helper functions ────────────────────────────────────────────────────

/**
 * Get the list of registered viewIds. Useful for sanity checks
 * (e.g. confirming a consumer's viewId is registered).
 */
export function listFilterViewIds() {
  return Object.keys(FILTER_CONFIGS);
}

/**
 * Get the config for a viewId, or null if not registered.
 */
export function getFilterConfig(viewId) {
  return FILTER_CONFIGS[viewId] || null;
}

/**
 * Build the initial values object for a viewId — every field set to
 * its "All" sentinel (the first option).
 */
function buildInitialValues(viewId) {
  const cfg = FILTER_CONFIGS[viewId];
  if (!cfg) return null;
  const initial = {};
  cfg.fields.forEach((f) => { initial[f.id] = f.options[0]; });
  return initial;
}

// ── useFilterState hook ─────────────────────────────────────────────────
// React idiom for per-view filter state. Replaces sandbox's
// window.FILTER_STATE global. State resets on component unmount /
// workspace switch (correct: filters are scoped to a view session).
//
// Returns: [values, setValue, reset, activeCount]
//   values: { fieldId: currentValue, ... }
//   setValue(fieldId, value): update a single field
//   reset(): restore all fields to "All"
//   activeCount: number of fields not on the "All" sentinel

export function useFilterState(viewId) {
  const [values, setValues] = React.useState(() => buildInitialValues(viewId) || {});

  // If viewId changes (rare — usually a view re-mount), re-init.
  React.useEffect(() => {
    const initial = buildInitialValues(viewId);
    if (initial) setValues(initial);
  }, [viewId]);

  const setValue = React.useCallback((fieldId, value) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const reset = React.useCallback(() => {
    const initial = buildInitialValues(viewId);
    if (initial) setValues(initial);
  }, [viewId]);

  // Active = any field not on its "All" sentinel.
  const cfg = FILTER_CONFIGS[viewId];
  const activeCount = cfg
    ? cfg.fields.filter((f) => values[f.id] !== f.options[0]).length
    : 0;

  return [values, setValue, reset, activeCount];
}

// ── Inline styles ───────────────────────────────────────────────────────
// Visual idiom matches ShellNav.jsx: Arial font, navy text, cyan accent
// when active, light grey borders.

const styles = {
  bar: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 16px',
    backgroundColor: '#FAFBFC',
    border: '1px solid #E5E8EE',
    borderRadius: 8,
    marginBottom: 14,
    flexWrap: 'wrap',
    fontFamily: 'Arial, sans-serif',
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    color: '#5A6478',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginRight: 4,
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  fieldLabel: {
    fontSize: 11,
    color: '#5A6478',
    fontWeight: 600,
  },
  select: {
    fontSize: 11,
    padding: '3px 6px',
    borderRadius: 5,
    color: '#1A2035',
    fontFamily: 'Arial, sans-serif',
    cursor: 'pointer',
    outline: 'none',
  },
  selectInactive: {
    border: '1px solid #D0D5DD',
    backgroundColor: '#FFFFFF',
  },
  selectActive: {
    border: '1px solid #00B8D0',
    backgroundColor: '#E6F7FA',
  },
  resetBtn: {
    fontSize: 10,
    padding: '3px 10px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #D0D5DD',
    borderRadius: 5,
    color: '#5A6478',
    cursor: 'pointer',
    marginLeft: 'auto',
    fontFamily: 'Arial, sans-serif',
  },
  emptyHint: {
    marginLeft: 'auto',
    fontSize: 10,
    color: '#9AA3B2',
    fontStyle: 'italic',
  },
  fallback: {
    padding: '8px 12px',
    fontSize: 11,
    color: '#9AA3B2',
    fontStyle: 'italic',
    backgroundColor: '#FAFBFC',
    border: '1px dashed #E5E8EE',
    borderRadius: 8,
    marginBottom: 14,
    fontFamily: 'Arial, sans-serif',
  },
};

// ── XcFilterBar component ───────────────────────────────────────────────
// Props:
//   viewId (required): one of the keys in FILTER_CONFIGS
//   onChange (optional): (values) => void  — called whenever values change.
//                        Consumers wire this to their list-filtering logic.
//
// Renders null if viewId is unknown (with a console.warn in dev mode).
// Renders a soft fallback note if viewId is unknown AND the env is
// development (helps catch typos during build-out).

export default function XcFilterBar({ viewId, onChange }) {
  const cfg = FILTER_CONFIGS[viewId];
  const [values, setValue, reset, activeCount] = useFilterState(viewId);

  // Notify consumers of every change. Wrapped in effect so external
  // setState doesn't run during render.
  React.useEffect(() => {
    if (onChange && cfg) onChange(values);
  }, [values, cfg, onChange]);

  if (!cfg) {
    // Soft fallback for unknown viewIds — visible in development, harmless
    // in production. Helps catch typos when wiring a new consumer view.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[XcFilterBar] Unknown viewId: "${viewId}". Registered: ${Object.keys(FILTER_CONFIGS).join(', ')}`);
      return (
        <div style={styles.fallback}>
          XcFilterBar: viewId "{viewId}" not registered in FILTER_CONFIGS.
        </div>
      );
    }
    return null;
  }

  return (
    <div style={styles.bar} role="region" aria-label={`Filter: ${cfg.label}`}>
      <div style={styles.label}>⌕ Filter</div>

      {cfg.fields.map((f) => {
        const current = values[f.id];
        const isActive = current !== f.options[0];
        const selectStyle = {
          ...styles.select,
          ...(isActive ? styles.selectActive : styles.selectInactive),
        };
        return (
          <div key={f.id} style={styles.fieldGroup}>
            <label htmlFor={`xcf-${viewId}-${f.id}`} style={styles.fieldLabel}>
              {f.label}:
            </label>
            <select
              id={`xcf-${viewId}-${f.id}`}
              style={selectStyle}
              value={current}
              onChange={(e) => setValue(f.id, e.target.value)}
            >
              {f.options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );
      })}

      {activeCount > 0 ? (
        <button
          type="button"
          style={styles.resetBtn}
          onClick={reset}
          aria-label="Reset filters"
        >
          Reset ({activeCount})
        </button>
      ) : (
        <div style={styles.emptyHint}>no active filters</div>
      )}
    </div>
  );
}